import { useEffect, useMemo, useRef, useState } from 'react';
import { listSessions, type Decision, type Session } from '../../services/sessions';
import { SessionDecisionModal } from './SessionDecisionModal';

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    OK: 'bg-emerald-900 text-emerald-400 border-emerald-700',
    SUSPECT: 'bg-amber-900 text-amber-400 border-amber-700',
    KICKED: 'bg-orange-900 text-orange-400 border-orange-700',
    BLOCKED: 'bg-red-900 text-red-400 border-red-700',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded border ${colors[status] || 'bg-gray-800 text-gray-400 border-gray-700'}`}>
      {status}
    </span>
  );
}

function DecisionBadge({ decision, decisionReal }: { decision: Decision; decisionReal: Decision }) {
  const colors: Record<Decision, string> = {
    ALLOW: 'bg-emerald-900 text-emerald-400 border-emerald-700',
    FLAG:  'bg-amber-900 text-amber-400 border-amber-700',
    KICK:  'bg-orange-900 text-orange-400 border-orange-700',
    BLOCK: 'bg-red-900 text-red-400 border-red-700',
    FORCE_UPDATE: 'bg-blue-900 text-blue-300 border-blue-700',
  };
  // Sprint 10 — em beta log-only, decision != decision_real → mostra ambos
  // pra operador ver "o que TERIA acontecido sem beta".
  const beta = decision !== decisionReal;
  return (
    <div className="flex items-center gap-1">
      <span className={`text-xs px-2 py-0.5 rounded border ${colors[decision]}`}>
        {decision}
      </span>
      {beta && (
        <span
          className="text-[10px] text-gray-500"
          title={`Decisão real (sem BETA_MODE): ${decisionReal}`}
        >
          (real: {decisionReal})
        </span>
      )}
    </div>
  );
}

function RiskBar({ score, peak }: { score: number; peak: number }) {
  const pct = Math.min(100, Math.max(0, score));
  // ADR-029 thresholds: 0-30 verde, 31-60 âmbar, 61-85 laranja, 86+ vermelho.
  const color =
    score >= 86 ? 'bg-red-500' :
    score >= 61 ? 'bg-orange-500' :
    score >= 31 ? 'bg-amber-500' :
    'bg-emerald-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 bg-gray-800 rounded overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span
        className="text-xs text-gray-300 whitespace-nowrap tabular-nums"
        title="Risco atual / maior risco observado nesta sessão"
      >
        {score} <span className="text-gray-600">/</span> <strong className="text-orange-300">{peak}</strong>
      </span>
    </div>
  );
}

const decisionOptions: Array<Decision | 'ALL'> = ['ALL', 'ALLOW', 'FLAG', 'KICK', 'BLOCK', 'FORCE_UPDATE'];

const PAGE_SIZE = 100; // teto de página do backend (SessionService.h: limit 1..100)

export function Sessions() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onlyOnline, setOnlyOnline] = useState(false);
  const [decisionFilter, setDecisionFilter] = useState<Decision | 'ALL'>('ALL');
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  // Paginação por cursor: backend devolve next_cursor; 0 = não há próxima página.
  const [nextCursor, setNextCursor] = useState<number>(0);
  const [loadingMore, setLoadingMore] = useState(false);
  // Ao carregar páginas extras, pausamos o auto-refresh de 5s pra ele não
  // descartar o que já foi paginado (senão a lista voltaria pra 1ª página).
  const [paginated, setPaginated] = useState(false);
  const paginatedRef = useRef(false);
  paginatedRef.current = paginated;
  // Guarda de corrida: só a resposta da última requisição de 1ª página vale
  // (troca de filtro, reset "ao vivo" e o tick de 5s competem entre si).
  const reqIdRef = useRef(0);

  async function fetchFirstPage(spinner: boolean): Promise<void> {
    const myReq = ++reqIdRef.current;
    if (spinner) setLoading(true);
    try {
      const r = await listSessions({ only_online: onlyOnline, limit: PAGE_SIZE });
      if (myReq !== reqIdRef.current) return; // chegou resposta mais nova
      setSessions(r.data);
      setNextCursor(r.next_cursor ?? 0);
      setError(null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      if (myReq === reqIdRef.current) setError(msg || 'erro ao carregar sessões');
    } finally {
      if (spinner && myReq === reqIdRef.current) setLoading(false);
    }
  }

  useEffect(() => {
    setPaginated(false);
    paginatedRef.current = false;
    void fetchFirstPage(true);
    const timer = window.setInterval(() => {
      if (!paginatedRef.current) void fetchFirstPage(false);
    }, 5_000);
    return () => window.clearInterval(timer);
    // fetchFirstPage fecha sobre onlyOnline; recriado a cada troca de filtro.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyOnline]);

  async function loadMore(): Promise<void> {
    if (!nextCursor || loadingMore) return;
    const myReq = reqIdRef.current;
    setLoadingMore(true);
    setPaginated(true);
    paginatedRef.current = true; // fecha a janela até o próximo render
    try {
      const r = await listSessions({ only_online: onlyOnline, limit: PAGE_SIZE, cursor: nextCursor });
      if (myReq !== reqIdRef.current) return; // filtro mudou no meio → descarta
      setSessions((prev) => {
        const seen = new Set(prev.map((s) => s.id));
        return [...prev, ...r.data.filter((s) => !seen.has(s.id))];
      });
      setNextCursor(r.next_cursor ?? 0);
      setError(null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg || 'erro ao carregar mais sessões');
    } finally {
      setLoadingMore(false);
    }
  }

  function resumeLive(): void {
    setPaginated(false);
    paginatedRef.current = false;
    void fetchFirstPage(true);
  }

  const filtered = useMemo(() => {
    if (decisionFilter === 'ALL') return sessions;
    return sessions.filter((s) => s.decision === decisionFilter || s.decision_real === decisionFilter);
  }, [sessions, decisionFilter]);

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-bold text-gray-200">Sessões</h2>
        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-gray-400">
            <input
              type="checkbox"
              checked={onlyOnline}
              onChange={(e) => setOnlyOnline(e.target.checked)}
              className="rounded border-[#2a2f36] bg-[#161a1f]"
            />
            Apenas online (heartbeat &lt; 60s)
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-400">
            Decisão:
            <select
              value={decisionFilter}
              onChange={(e) => setDecisionFilter(e.target.value as Decision | 'ALL')}
              className="rounded border border-[#2a2f36] bg-[#161a1f] text-gray-200 px-2 py-1 text-sm"
            >
              {decisionOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </label>
        </div>
      </header>

      {error && (
        <div className="bg-red-950 border border-red-800 text-red-300 text-sm rounded px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Carregando...</p>
      ) : filtered.length === 0 ? (
        <p className="text-gray-500">Nenhuma sessão.</p>
      ) : (
        <div className="bg-[#161a1f] border border-[#2a2f36] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0d0f12] text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-4 py-3">Session</th>
                <th className="text-left px-4 py-3">User / Account</th>
                <th className="text-left px-4 py-3">IP</th>
                <th className="text-left px-4 py-3">Build</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Decisão</th>
                <th className="text-left px-4 py-3">Risco atual / pico</th>
                <th className="text-left px-4 py-3">Last HB</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2a2f36]">
              {filtered.map((s) => (
                <tr
                  key={s.id}
                  className="hover:bg-[#0d0f12] cursor-pointer"
                  onClick={() => setSelectedSession(s)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedSession(s);
                    }
                  }}
                  aria-label={`Abrir decisão da sessão ${s.session_id.slice(0, 8)}`}
                >
                  <td className="px-4 py-3 font-mono text-xs text-gray-400" title={s.session_id}>
                    {s.session_id.slice(0, 8)}…
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    {s.user_id || '—'}
                    {s.account_id && (
                      <span className="text-xs text-gray-500 ml-1">/ {s.account_id}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-400">{s.ip || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-400">{s.client_build || '—'}</td>
                  <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                  <td className="px-4 py-3">
                    <DecisionBadge decision={s.decision} decisionReal={s.decision_real} />
                  </td>
                  <td className="px-4 py-3">
                    <RiskBar score={s.risk_score} peak={s.peak_risk_score ?? s.risk_score} />
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400" title={s.last_heartbeat_at}>
                    {new Date(s.last_heartbeat_at).toLocaleTimeString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="mt-3 flex items-center justify-between gap-3 text-sm text-gray-500">
          <span>
            Mostrando {filtered.length} sess{filtered.length === 1 ? 'ão' : 'ões'}
            {decisionFilter !== 'ALL' && ` (filtro ${decisionFilter} · ${sessions.length} carregadas)`}
            {paginated && ' · atualização automática pausada'}
          </span>
          <div className="flex items-center gap-2">
            {paginated && (
              <button
                type="button"
                onClick={resumeLive}
                className="px-3 py-1.5 rounded border border-[#2a2f36] bg-[#161a1f] text-gray-300 hover:bg-[#0d0f12]"
              >
                Voltar ao vivo
              </button>
            )}
            {nextCursor > 0 ? (
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={loadingMore}
                className="px-3 py-1.5 rounded border border-[#2a2f36] bg-[#161a1f] text-gray-200 hover:bg-[#0d0f12] disabled:opacity-50"
              >
                {loadingMore ? 'Carregando…' : 'Carregar mais'}
              </button>
            ) : (
              <span className="text-gray-600">fim da lista</span>
            )}
          </div>
        </div>
      )}

      {selectedSession && (
        <SessionDecisionModal
          // Backend Session.id = uuid (externo, ex 1f4a61ab-...). NAO confundir
          // com session_id (VARCHAR SDK-generated, ex 73523995-...).
          // Endpoints /admin/sessions/{uuid}/* esperam o uuid (= Session.id).
          sessionUuid={selectedSession.id}
          initialHint={{
            risk_score: selectedSession.risk_score,
            peak_risk_score: selectedSession.peak_risk_score ?? selectedSession.risk_score,
            decision: selectedSession.decision,
            decision_real: selectedSession.decision_real,
            user_id: selectedSession.user_id || '',
            account_id: selectedSession.account_id || selectedSession.user_id || '',
            hwid: selectedSession.hwid || '',
          }}
          onClose={() => setSelectedSession(null)}
        />
      )}
    </div>
  );
}
